import { useContext, useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  CheckBox,
  FormField,
  Grid,
  Layer,
  ResponsiveContext,
  Select,
  Spinner,
  Text,
  TextArea,
  TextInput,
} from "grommet";
import {
  fetchPodSession,
  fetchServerTime,
  fetchCBRegistration,
  invokeSubmitPodProcessor,
} from "./api";
import {
  CENTRAL_DATETIME_FORMAT,
  getFlagOptions,
  formatCentralDateTime,
  isCentralEndTimeAfterStartTime,
  isValidCentralDateTimeFormat,
  parsePodSessionNotes,
  serializePodSessionNotes,
} from "./notesUtils";
import {
  NOTE_KEYS,
  applyRemotelyChangedNotes,
  mergeNotesForSave,
  mergeRemoteNotesIntoLocal,
  valuesEqual,
  isEmptyNotesValue,
} from "./notesMergeUtils";
import {
  isFinishedSessionNotesLocked,
  shouldShowReceiptField,
  isCollegeBoardOrganization,
  getKeycodeFromFields,
} from "./sessionUtils";
import { useAutosaveQueue } from "./useAutosaveQueue";
import CollegeBoardAddIssueLayer from "./CollegeBoardAddIssueLayer";
import OrangeFlagInfoLayer from "./OrangeFlagInfoLayer";
import { appendCollegeBoardIssueToNotes } from "./collegeBoardIssues";

const FLAG_COLORS = {
  Green: "#90EE90",
  Yellow: "#FFFF99",
  Orange: "#FFC966",
  Red: "#FFB6B6",
  DNC: "#FFD699",
  RECON: "#FFD699",
  DNS: "#D8BFD8",
  MOD: "#FFB6B6",
};

const inputOutlineStyle = {
  border: "1px solid #CCCCCC",
  borderRadius: "8px",
  boxSizing: "border-box",
};

const SUBMIT_REQUIRED_FIELDS = [
  { key: "flag", label: "Flag", isVisible: () => true },
  { key: "exam", label: "Exam", isVisible: () => true },
  {
    key: "receipt",
    label: "Receipt #",
    isVisible: (podNotesFormat, podSession) =>
      shouldShowReceiptField(podSession, podNotesFormat),
  },
  {
    key: "instructor",
    label: "Instructor",
    isVisible: (podNotesFormat) => podNotesFormat?.instructor === true,
  },
  {
    key: "score",
    label: "Score",
    isVisible: (podNotesFormat) => podNotesFormat?.score === true,
  },
  {
    key: "examCode",
    label: "Exam Code",
    isVisible: (podNotesFormat) => podNotesFormat?.examCode === true,
  },
  { key: "startTime", label: "Start Time", isVisible: () => true },
  { key: "endTime", label: "End Time", isVisible: () => true },
  { key: "notes", label: "Notes", isVisible: () => true },
];

const SUBMIT_REQUIRED_CHECKBOXES = [
  { key: "idCheck", label: "ID check" },
  { key: "roomCheck", label: "Room check" },
  { key: "personCheck", label: "Wrists, ears, etc. check" },
];

const TIME_FIELD_KEYS = ["startTime", "endTime"];

function isMissingSubmitProctor(proctor) {
  if (proctor == null) return true;

  const normalizedProctor = String(proctor).trim().toUpperCase();
  return (
    normalizedProctor === "" ||
    normalizedProctor === "NULL" ||
    normalizedProctor === "UNCLAIMED"
  );
}

function isMissingSubmitText(value) {
  if (value == null) return true;

  const normalizedValue = String(value).trim();
  return normalizedValue === "" || normalizedValue.toUpperCase() === "NULL";
}

function NotesCheckBox({ label, checked, onChange, disabled }) {
  return (
    <CheckBox
      label={<Text size="small">{label}</Text>}
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

function isSessionIdentityVerified(podSession) {
  return Boolean(String(podSession?.verified_by ?? "").trim());
}

function shouldAutoCheckIdNotes({ idCheckOverride, podSession, isUnclaimed }) {
  if (isUnclaimed) return false;
  return idCheckOverride === true || isSessionIdentityVerified(podSession);
}

function getTimeValidationErrors(notesState) {
  const errors = {};
  const startTime = String(notesState.startTime ?? "").trim();
  const endTime = String(notesState.endTime ?? "").trim();

  if (startTime && !isValidCentralDateTimeFormat(startTime)) {
    errors.startTime = `Start Time must use ${CENTRAL_DATETIME_FORMAT} in US Central time.`;
  }

  if (endTime && !isValidCentralDateTimeFormat(endTime)) {
    errors.endTime = `End Time must use ${CENTRAL_DATETIME_FORMAT} in US Central time.`;
  }

  if (
    startTime &&
    endTime &&
    !errors.startTime &&
    !errors.endTime &&
    !isCentralEndTimeAfterStartTime(startTime, endTime)
  ) {
    errors.endTime = "End Time must be greater than Start Time.";
  }

  return errors;
}

function mergeTimeValidationErrors(prevErrors, notesState) {
  const nextErrors = { ...prevErrors };
  TIME_FIELD_KEYS.forEach((key) => {
    delete nextErrors[key];
  });

  return {
    ...nextErrors,
    ...getTimeValidationErrors(notesState),
  };
}

function getKeysToSave(localNotes, remoteNotes, dirtyKeys) {
  const keys = new Set(dirtyKeys);

  NOTE_KEYS.forEach((key) => {
    if (keys.has(key)) return;
    if (valuesEqual(localNotes?.[key], remoteNotes?.[key])) return;
    // Never push an empty local value over a non-empty remote unless dirty.
    if (
      isEmptyNotesValue(localNotes?.[key]) &&
      !isEmptyNotesValue(remoteNotes?.[key])
    ) {
      return;
    }
    keys.add(key);
  });

  return keys;
}

function TimeFormatHint({ currentCentralTime }) {
  return (
    <Text size="small" color="dark-4" margin={{ top: "xsmall" }}>
      Format: {CENTRAL_DATETIME_FORMAT}. Current Central time:{" "}
      {currentCentralTime}
    </Text>
  );
}

function getSubmitValidationErrors(
  notesState,
  podNotesFormat,
  proctor,
  googleMeetLink,
  podSession,
) {
  const errors = {};

  if (isMissingSubmitProctor(proctor)) {
    errors.proctor = "Proctor is required before submitting.";
  }

  if (isMissingSubmitText(googleMeetLink)) {
    errors.googleMeetLink = "Google Meet link is required before submitting.";
  }

  SUBMIT_REQUIRED_FIELDS.forEach(({ key, label, isVisible }) => {
    if (!isVisible(podNotesFormat, podSession)) return;

    const value = notesState[key];
    if (value == null || String(value).trim() === "") {
      errors[key] = `${label} is required before submitting.`;
    }
  });

  SUBMIT_REQUIRED_CHECKBOXES.forEach(({ key, label }) => {
    if (notesState[key] !== true) {
      errors[key] = `${label} is required before submitting.`;
    }
  });

  return errors;
}

function StatusMessage({ status, message }) {
  if (!status || status === "loading") {
    return null;
  }

  return (
    <Text
      size="small"
      color={status === "success" ? "status-ok" : "status-critical"}
    >
      {message}
    </Text>
  );
}

export default function PodNotesCard({
  podSession,
  podNotesFormat,
  agentAlertExamOverride,
  agentAlertInstructorOverride,
  idCheckOverride,
  onSave,
  onSyncMasterSheet,
  currentUsername,
  user,
  userEmail,
  isUnclaimed = false,
}) {
  const size = useContext(ResponsiveContext);
  const isNarrow = size === "small";
  const showReceipt = shouldShowReceiptField(podSession, podNotesFormat);
  const showInstructor = podNotesFormat?.instructor === true;
  const showScore = podNotesFormat?.score === true;
  const showExamCode = podNotesFormat?.examCode === true;
  const showStudentId = isCollegeBoardOrganization(podSession?.organizationId);
  const collegeBoardKeycode = showStudentId
    ? getKeycodeFromFields(podSession?.fields)
    : "";
  const scoreInstructions = podNotesFormat?.scoreInstructions?.trim?.() ?? "";
  const [notesState, setNotesState] = useState(() =>
    parsePodSessionNotes(podSession?.notes, podSession?.fields),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitModal, setSubmitModal] = useState(null);
  const [showAddIssueModal, setShowAddIssueModal] = useState(false);
  const [showOrangeFlagInfo, setShowOrangeFlagInfo] = useState(false);
  const [claimRequiredAlert, setClaimRequiredAlert] = useState(false);
  const [serverTimeLoading, setServerTimeLoading] = useState(false);
  const serverTimeRequestInFlightRef = useRef(false);
  const [currentCentralTime, setCurrentCentralTime] = useState(() =>
    formatCentralDateTime(),
  );

  const notesStateRef = useRef(notesState);
  const dirtyKeysRef = useRef(new Set());
  const valueOnFocusRef = useRef({});
  const lastRemoteUpdatedAtRef = useRef(podSession?.updatedAt ?? null);
  const lastAppliedRemoteNotesRef = useRef(
    parsePodSessionNotes(podSession?.notes, podSession?.fields),
  );
  // Snapshot for session-switch reset only; remote sync uses the merge effect below.
  const podSessionRef = useRef(podSession);
  podSessionRef.current = podSession;
  const isNotesLocked = isFinishedSessionNotesLocked(podSession);
  const isWriteLocked = isNotesLocked || Boolean(isUnclaimed);

  useEffect(() => {
    notesStateRef.current = notesState;
  }, [notesState]);

  // Reset local form state only when navigating to a different session.
  // Do not depend on notes/fields/updatedAt here — that would wipe dirty
  // edits; same-session remote updates are merged in the effect below.
  useEffect(() => {
    const session = podSessionRef.current;
    dirtyKeysRef.current = new Set();
    valueOnFocusRef.current = {};
    lastRemoteUpdatedAtRef.current = session?.updatedAt ?? null;
    const initialNotes = parsePodSessionNotes(session?.notes, session?.fields);
    lastAppliedRemoteNotesRef.current = initialNotes;
    setNotesState(initialNotes);
    setSaveError(null);
    setFieldErrors({});
  }, [podSession?.id]);

  useEffect(() => {
    if (!podSession?.id) return;
    if (podSession.updatedAt === lastRemoteUpdatedAtRef.current) return;

    lastRemoteUpdatedAtRef.current = podSession.updatedAt ?? null;

    const remoteNotes = parsePodSessionNotes(
      podSession.notes,
      podSession.fields,
    );
    const previousRemoteNotes = lastAppliedRemoteNotesRef.current;

    setNotesState((prev) => {
      const { notes, changedKeys } = applyRemotelyChangedNotes(
        prev,
        previousRemoteNotes,
        remoteNotes,
        { dirtyKeys: dirtyKeysRef.current },
      );

      changedKeys.forEach((key) => {
        dirtyKeysRef.current.delete(key);
      });

      return notes;
    });

    lastAppliedRemoteNotesRef.current = remoteNotes;
  }, [
    podSession?.id,
    podSession?.notes,
    podSession?.updatedAt,
    podSession?.fields,
  ]);

  useEffect(() => {
    if (!isUnclaimed) {
      setClaimRequiredAlert(false);
    }
  }, [isUnclaimed]);

  useEffect(() => {
    if (isUnclaimed) return;
    if (agentAlertExamOverride === undefined) return;

    setNotesState((prev) => ({ ...prev, exam: agentAlertExamOverride }));
    dirtyKeysRef.current.add("exam");
    setSaveError(null);
    clearFieldError("exam");
  }, [agentAlertExamOverride, isUnclaimed]);

  useEffect(() => {
    if (isUnclaimed) return;
    if (agentAlertInstructorOverride === undefined) return;

    setNotesState((prev) => ({
      ...prev,
      instructor: agentAlertInstructorOverride,
    }));
    dirtyKeysRef.current.add("instructor");
    setSaveError(null);
    clearFieldError("instructor");
  }, [agentAlertInstructorOverride, isUnclaimed]);

  useEffect(() => {
    if (
      !shouldAutoCheckIdNotes({ idCheckOverride, podSession, isUnclaimed })
    ) {
      return;
    }

    if (notesStateRef.current.idCheck) return;

    setNotesState((prev) => ({ ...prev, idCheck: true }));
    dirtyKeysRef.current.add("idCheck");
    setSaveError(null);
  }, [idCheckOverride, isUnclaimed, podSession?.verified_by]);

  useEffect(() => {
    if (!showStudentId || !podSession?.id || !collegeBoardKeycode) return;

    let cancelled = false;

    (async () => {
      try {
        const registration = await fetchCBRegistration(collegeBoardKeycode);
        if (cancelled || !registration) return;

        const testName = String(registration.test_name ?? "").trim();
        const studentId = String(registration.student_id ?? "").trim();
        if (!testName && !studentId) return;

        const current = notesStateRef.current;
        const updates = {};

        if (
          testName &&
          isEmptyNotesValue(current.exam) &&
          !dirtyKeysRef.current.has("exam")
        ) {
          updates.exam = testName;
        }

        if (
          studentId &&
          isEmptyNotesValue(current.student_id) &&
          !dirtyKeysRef.current.has("student_id")
        ) {
          updates.student_id = studentId;
        }

        const populatedKeys = Object.keys(updates);
        if (cancelled || populatedKeys.length === 0) return;

        populatedKeys.forEach((key) => dirtyKeysRef.current.add(key));
        setNotesState((prev) => ({ ...prev, ...updates }));
        setSaveError(null);
        enqueueAutosaveRef.current?.();
      } catch (error) {
        console.error("Error fetching College Board registration:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showStudentId, podSession?.id, collegeBoardKeycode]);

  useEffect(() => {
    if (!isMissingSubmitProctor(podSession?.proctor)) {
      clearFieldError("proctor");
    }

    if (!isMissingSubmitText(podSession?.googleMeetLink)) {
      clearFieldError("googleMeetLink");
    }
  }, [podSession?.googleMeetLink, podSession?.proctor]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentCentralTime(formatCentralDateTime());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const markDirty = useCallback((key) => {
    dirtyKeysRef.current.add(key);
  }, []);

  const suppressLockedClickRef = useRef(false);

  const showClaimRequiredAlert = useCallback(() => {
    if (!isUnclaimed) return;
    setClaimRequiredAlert(true);
  }, [isUnclaimed]);

  const handleLockedAreaPointer = useCallback(
    (event) => {
      if (!isUnclaimed) return;

      // Ignore interactions on the notice itself (e.g. Close).
      if (event?.target?.closest?.("[data-claim-required-notice]")) {
        return;
      }

      if (event?.type === "mousedown") {
        showClaimRequiredAlert();
        suppressLockedClickRef.current = true;
        return;
      }

      if (event?.type === "click") {
        if (suppressLockedClickRef.current) {
          suppressLockedClickRef.current = false;
          return;
        }
        showClaimRequiredAlert();
      }
    },
    [isUnclaimed, showClaimRequiredAlert],
  );

  const updateField = (key, value) => {
    if (isUnclaimed) return;

    setNotesState((prev) => {
      const nextNotesState = { ...prev, [key]: value };

      if (TIME_FIELD_KEYS.includes(key)) {
        setFieldErrors((prevErrors) =>
          mergeTimeValidationErrors(prevErrors, nextNotesState),
        );
      }

      return nextNotesState;
    });
    setSaveError(null);

    if (!TIME_FIELD_KEYS.includes(key)) {
      clearFieldError(key);
    }
  };

  const persistNotes = useCallback(
    async ({ forceAllDiffering = false, status } = {}) => {
      if (!onSave || !podSession?.id || isWriteLocked) return null;

      const localNotes = notesStateRef.current;
      const timeErrors = getTimeValidationErrors(localNotes);
      const dirtyTimeKeys = TIME_FIELD_KEYS.filter((key) =>
        dirtyKeysRef.current.has(key),
      );
      const mustValidateTimes =
        forceAllDiffering ||
        status !== undefined ||
        dirtyTimeKeys.some((key) => timeErrors[key]);

      if (Object.keys(timeErrors).length > 0 && mustValidateTimes) {
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          ...timeErrors,
        }));
        setSaveError("Fix time validation errors before saving.");
        throw new Error("Time validation failed");
      }

      setFieldErrors((prevErrors) => {
        const nextErrors = { ...prevErrors };
        TIME_FIELD_KEYS.forEach((key) => {
          delete nextErrors[key];
        });
        return nextErrors;
      });
      setSaving(true);
      setSaveError(null);

      try {
        const latest = await fetchPodSession(podSession.id);
        const remoteNotes = parsePodSessionNotes(
          latest?.notes,
          latest?.fields ?? podSession.fields,
        );
        const keysToSave = forceAllDiffering
          ? getKeysToSave(localNotes, remoteNotes, dirtyKeysRef.current)
          : new Set(dirtyKeysRef.current);

        if (keysToSave.size === 0 && status === undefined) {
          return latest ?? podSession;
        }

        // Submit must persist the full local form (finished status).
        const dirtyKeysForMerge =
          status !== undefined ? new Set(NOTE_KEYS) : keysToSave;

        const mergedNotes = mergeNotesForSave(remoteNotes, localNotes, {
          dirtyKeys: dirtyKeysForMerge,
        });

        const options = status !== undefined ? { status } : {};
        const updated = await onSave(
          serializePodSessionNotes(mergedNotes),
          options,
        );

        dirtyKeysForMerge.forEach((key) => {
          dirtyKeysRef.current.delete(key);
        });

        if (updated?.updatedAt) {
          lastRemoteUpdatedAtRef.current = updated.updatedAt;
        }

        lastAppliedRemoteNotesRef.current = mergedNotes;

        setNotesState((prev) =>
          mergeRemoteNotesIntoLocal(prev, mergedNotes, {
            dirtyKeys: dirtyKeysRef.current,
            // Saved fields were just written; remaining dirty keys stay local.
            localHasPriority: true,
          }),
        );

        return updated;
      } catch (error) {
        console.error(error);
        setSaveError("Failed to save notes. Please try again.");
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [isWriteLocked, onSave, podSession],
  );

  const { enqueue: enqueueAutosave, cancel: cancelAutosave } = useAutosaveQueue(
    {
      enabled: !isWriteLocked,
      onFlush: async () => {
        if (dirtyKeysRef.current.size === 0) return;
        try {
          await persistNotes();
        } catch (error) {
          // Validation or network errors keep dirty keys for a later retry.
          console.error(error);
        }
      },
    },
  );

  const enqueueAutosaveRef = useRef(enqueueAutosave);
  useEffect(() => {
    enqueueAutosaveRef.current = enqueueAutosave;
  }, [enqueueAutosave]);

  useEffect(() => {
    if (isUnclaimed) return;
    if (agentAlertExamOverride === undefined) return;
    enqueueAutosaveRef.current?.();
  }, [agentAlertExamOverride, isUnclaimed]);

  useEffect(() => {
    if (isUnclaimed) return;
    if (agentAlertInstructorOverride === undefined) return;
    enqueueAutosaveRef.current?.();
  }, [agentAlertInstructorOverride, isUnclaimed]);

  useEffect(() => {
    if (
      !shouldAutoCheckIdNotes({ idCheckOverride, podSession, isUnclaimed })
    ) {
      return;
    }

    if (notesStateRef.current.idCheck) return;

    enqueueAutosaveRef.current?.();
  }, [idCheckOverride, isUnclaimed, podSession?.verified_by]);

  const handleFieldBlur = (key) => {
    if (isUnclaimed) return;

    const focusedValue = valueOnFocusRef.current[key];
    const currentValue = notesStateRef.current[key];

    if (valuesEqual(focusedValue, currentValue)) return;

    markDirty(key);
    enqueueAutosave();
  };

  const handleFieldFocus = (key) => {
    if (isUnclaimed) return;
    valueOnFocusRef.current[key] = notesStateRef.current[key];
  };

  const updateFieldAndAutosave = (key, value) => {
    if (isUnclaimed) return;
    markDirty(key);
    updateField(key, value);
    enqueueAutosave();
  };

  const flagOptions = getFlagOptions(podSession?.organizationId);

  const handleFlagChange = (option) => {
    if (isUnclaimed) return;
    if (option !== "" && !flagOptions.includes(option)) return;

    updateFieldAndAutosave("flag", option);
    if (option === "Orange") {
      setShowOrangeFlagInfo(true);
    }
  };

  const handleAddCollegeBoardIssue = (category) => {
    if (isWriteLocked) return;

    const nextNotes = appendCollegeBoardIssueToNotes(
      notesStateRef.current.notes,
      category,
    );
    notesStateRef.current = {
      ...notesStateRef.current,
      notes: nextNotes,
    };
    markDirty("notes");
    updateField("notes", nextNotes);
    enqueueAutosave();
    setShowAddIssueModal(false);

    window.setTimeout(() => {
      const notesInput = document.getElementById("pod-notes-text");
      if (!notesInput) return;
      notesInput.focus();
      const cursor = nextNotes.length;
      notesInput.setSelectionRange(cursor, cursor);
    }, 0);
  };

  const handleSetTimeToNow = async (field) => {
    if (isUnclaimed || serverTimeRequestInFlightRef.current) return;

    serverTimeRequestInFlightRef.current = true;
    setServerTimeLoading(true);
    setSaveError(null);

    try {
      const serverTimestamp = await fetchServerTime();
      const centralNow = formatCentralDateTime(serverTimestamp);
      markDirty(field);
      updateField(field, centralNow);
      enqueueAutosave();
    } catch (error) {
      console.error(error);
      setSaveError("Could not get server time. Please try again.");
    } finally {
      serverTimeRequestInFlightRef.current = false;
      setServerTimeLoading(false);
    }
  };

  const handleSubmit = () => {
    if (isWriteLocked) return;

    const errors = {
      ...getSubmitValidationErrors(
        notesState,
        podNotesFormat,
        podSession?.proctor,
        podSession?.googleMeetLink,
        podSession,
      ),
      ...getTimeValidationErrors(notesState),
    };
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setSubmitModal({
      phase: "confirm",
      saveStatus: null,
      sheetStatus: null,
      saveMessage: "",
      sheetMessage: "",
    });
  };

  const closeSubmitModal = () => {
    setSubmitModal(null);
  };

  const handleConfirmSubmit = async () => {
    if (isWriteLocked) return;
    if (!onSave || !onSyncMasterSheet) return;

    cancelAutosave();

    setSubmitModal({
      phase: "processing",
      saveStatus: "loading",
      sheetStatus: null,
      saveMessage: "",
      sheetMessage: "",
    });

    let updatedSession;
    try {
      updatedSession = await persistNotes({ status: "FINISHED" });
      setSubmitModal((prev) => ({
        ...prev,
        saveStatus: "success",
        saveMessage: "Notes saved and session marked finished.",
        sheetStatus: "loading",
      }));
    } catch (error) {
      console.error(error);
      setSubmitModal((prev) => ({
        ...prev,
        phase: "done",
        saveStatus: "error",
        saveMessage: "Failed to save notes. Please try again.",
      }));
      return;
    }

    const sessionForSync = updatedSession ?? {
      ...podSession,
      notes: serializePodSessionNotes(notesState),
      status: "FINISHED",
    };

    console.log("submitPodProcessor → calling", { id: sessionForSync?.id });
    try {
      const processorResult = await invokeSubmitPodProcessor(sessionForSync);
      console.log("submitPodProcessor result:", processorResult);
    } catch (error) {
      console.error("submitPodProcessor error:", error);
    }

    try {
      const sessionForSync = updatedSession ?? {
        ...podSession,
        notes: serializePodSessionNotes(notesStateRef.current),
        status: "FINISHED",
      };
      const sheetResult = await onSyncMasterSheet(sessionForSync);
      const actionLabel =
        sheetResult.action === "updated" ? "updated in" : "added to";
      setSubmitModal((prev) => ({
        ...prev,
        phase: "done",
        sheetStatus: "success",
        sheetMessage: `Session ${actionLabel} master sheet${
          sheetResult.rowNumber ? ` (row ${sheetResult.rowNumber})` : ""
        }.`,
      }));
    } catch (error) {
      console.error(error);
      setSubmitModal((prev) => ({
        ...prev,
        phase: "done",
        sheetStatus: "error",
        sheetMessage:
          error.message ||
          "Failed to sync with master sheet. Please try again.",
      }));
    }
  };

  const handleSave = async () => {
    if (isWriteLocked) return;
    cancelAutosave();
    try {
      await persistNotes({ forceAllDiffering: true });
    } catch (error) {
      // persistNotes already sets saveError
    }
  };

  const isProcessing = submitModal?.phase === "processing";
  const isDone = submitModal?.phase === "done";
  const submitAreaErrorMessages = Object.values(fieldErrors).filter(Boolean);

  return (
    <>
      {submitModal && (
        <Layer
          position="center"
          modal
          onEsc={!isProcessing ? closeSubmitModal : undefined}
          onClickOutside={!isProcessing ? closeSubmitModal : undefined}
        >
          <Box pad="medium" gap="medium" width="medium">
            <Text weight="bold" size="large">
              Submit Session
            </Text>

            {submitModal.phase === "confirm" && (
              <Text>
                By submitting you are confirming that you have completed this
                session.
              </Text>
            )}

            {(submitModal.phase === "processing" || isDone) && (
              <Box gap="small">
                <Box direction="row" align="center" gap="small">
                  {submitModal.saveStatus === "loading" && (
                    <Spinner size="small" />
                  )}
                  <Text weight="bold">Step 1: Save notes</Text>
                </Box>
                <StatusMessage
                  status={submitModal.saveStatus}
                  message={submitModal.saveMessage}
                />

                {(submitModal.sheetStatus ||
                  submitModal.saveStatus === "success") && (
                  <>
                    <Box direction="row" align="center" gap="small">
                      {submitModal.sheetStatus === "loading" && (
                        <Spinner size="small" />
                      )}
                      <Text weight="bold">Step 2: Sync master sheet</Text>
                    </Box>
                    <StatusMessage
                      status={submitModal.sheetStatus}
                      message={submitModal.sheetMessage}
                    />
                  </>
                )}
              </Box>
            )}

            <Box direction="row" gap="small" justify="end">
              {submitModal.phase === "confirm" && (
                <>
                  <Button label="No" onClick={closeSubmitModal} />
                  <Button
                    label="Yes"
                    primary
                    color="black"
                    onClick={handleConfirmSubmit}
                  />
                </>
              )}
              {isDone && (
                <Button label="Close" primary onClick={closeSubmitModal} />
              )}
            </Box>
          </Box>
        </Layer>
      )}

      {showAddIssueModal && (
        <CollegeBoardAddIssueLayer
          onClose={() => setShowAddIssueModal(false)}
          onAddIssue={handleAddCollegeBoardIssue}
        />
      )}

      {showOrangeFlagInfo && (
        <OrangeFlagInfoLayer onClose={() => setShowOrangeFlagInfo(false)} />
      )}

      <Card fill>
        <CardHeader
          pad="medium"
          border={{ side: "bottom", color: "gray", size: "xsmall" }}
        >
          <Text weight="bold" size="large">
            Notes
          </Text>
        </CardHeader>

        <CardBody pad="medium" gap="medium">
          {claimRequiredAlert && (
            <Box
              data-claim-required-notice
              background="status-critical"
              pad="small"
              round="small"
              role="alert"
              direction="row"
              align="center"
              justify="between"
              gap="small"
            >
              <Text color="white" weight="bold" size="small">
                This session is locked until it's claimed
              </Text>
              <Button
                label="Close"
                size="small"
                plain
                style={{ color: "white", fontWeight: "bold" }}
                onClick={() => setClaimRequiredAlert(false)}
                a11yTitle="Close locked session notice"
              />
            </Box>
          )}
          <Box
            gap="medium"
            onClickCapture={isUnclaimed ? handleLockedAreaPointer : undefined}
            onMouseDownCapture={
              isUnclaimed ? handleLockedAreaPointer : undefined
            }
            style={
              isUnclaimed
                ? {
                    opacity: 0.55,
                  }
                : undefined
            }
            aria-disabled={isUnclaimed || undefined}
          >
            <Grid
              columns={isNarrow ? ["full"] : ["1/2", "1/2"]}
              gap="medium"
              rows={["auto"]}
            >
              <FormField
                label="Flag"
                htmlFor="pod-notes-flag"
                error={fieldErrors.flag}
              >
                <Select
                  id="pod-notes-flag"
                  options={["", ...flagOptions]}
                  value={notesState.flag}
                  disabled={isWriteLocked}
                  onChange={({ option }) => handleFlagChange(option)}
                  style={{
                    ...inputOutlineStyle,
                    backgroundColor: FLAG_COLORS[notesState.flag] ?? "white",
                  }}
                />
              </FormField>

              {showReceipt && (
                <FormField
                  label="Receipt #"
                  htmlFor="pod-notes-receipt"
                  error={fieldErrors.receipt}
                >
                  <TextInput
                    id="pod-notes-receipt"
                    value={notesState.receipt}
                    disabled={isWriteLocked}
                    onChange={(event) =>
                      updateField("receipt", event.target.value)
                    }
                    onFocus={() => handleFieldFocus("receipt")}
                    onBlur={() => handleFieldBlur("receipt")}
                    style={inputOutlineStyle}
                  />
                </FormField>
              )}

              <FormField
                label="Exam"
                htmlFor="pod-notes-exam"
                error={fieldErrors.exam}
              >
                <TextInput
                  id="pod-notes-exam"
                  value={notesState.exam}
                  disabled={isWriteLocked}
                  onChange={(event) => updateField("exam", event.target.value)}
                  onFocus={() => handleFieldFocus("exam")}
                  onBlur={() => handleFieldBlur("exam")}
                  style={inputOutlineStyle}
                />
              </FormField>

              {showInstructor && (
                <FormField
                  label="Instructor"
                  htmlFor="pod-notes-instructor"
                  error={fieldErrors.instructor}
                >
                  <TextInput
                    id="pod-notes-instructor"
                    value={notesState.instructor}
                    disabled={isWriteLocked}
                    onChange={(event) =>
                      updateField("instructor", event.target.value)
                    }
                    onFocus={() => handleFieldFocus("instructor")}
                    onBlur={() => handleFieldBlur("instructor")}
                    style={inputOutlineStyle}
                  />
                </FormField>
              )}

              {showExamCode && (
                <FormField
                  label="Exam Code"
                  htmlFor="pod-notes-exam-code"
                  error={fieldErrors.examCode}
                >
                  <TextInput
                    id="pod-notes-exam-code"
                    value={notesState.examCode}
                    disabled={isWriteLocked}
                    onChange={(event) =>
                      updateField("examCode", event.target.value)
                    }
                    onFocus={() => handleFieldFocus("examCode")}
                    onBlur={() => handleFieldBlur("examCode")}
                    style={inputOutlineStyle}
                  />
                </FormField>
              )}

              {showScore && (
                <FormField
                  label="Score"
                  htmlFor="pod-notes-score"
                  error={fieldErrors.score}
                >
                  <TextInput
                    id="pod-notes-score"
                    value={notesState.score}
                    disabled={isWriteLocked}
                    onChange={(event) =>
                      updateField("score", event.target.value)
                    }
                    onFocus={() => handleFieldFocus("score")}
                    onBlur={() => handleFieldBlur("score")}
                    style={inputOutlineStyle}
                  />
                  {scoreInstructions && (
                    <Text
                      size="small"
                      color="dark-4"
                      margin={{ top: "xsmall" }}
                    >
                      {scoreInstructions}
                    </Text>
                  )}
                </FormField>
              )}

              <FormField
                label="Start Time (US Central)"
                htmlFor="pod-notes-start"
                error={fieldErrors.startTime}
              >
                <Box direction="row" gap="small" align="center">
                  <Box flex>
                    <TextInput
                      id="pod-notes-start"
                      value={notesState.startTime}
                      placeholder="MM/DD/YYYY, hh:mm:ss A"
                      disabled={isWriteLocked}
                      onChange={(event) =>
                        updateField("startTime", event.target.value)
                      }
                      onFocus={() => handleFieldFocus("startTime")}
                      onBlur={() => handleFieldBlur("startTime")}
                      style={inputOutlineStyle}
                    />
                  </Box>
                  <Button
                    label="Now"
                    size="small"
                    primary={false}
                    disabled={isWriteLocked || serverTimeLoading}
                    onClick={() => handleSetTimeToNow("startTime")}
                  />
                </Box>
                <TimeFormatHint currentCentralTime={currentCentralTime} />
              </FormField>

              <FormField
                label="End Time (US Central)"
                htmlFor="pod-notes-end"
                error={fieldErrors.endTime}
              >
                <Box direction="row" gap="small" align="center">
                  <Box flex>
                    <TextInput
                      id="pod-notes-end"
                      value={notesState.endTime}
                      placeholder="MM/DD/YYYY, hh:mm:ss A"
                      disabled={isWriteLocked}
                      onChange={(event) =>
                        updateField("endTime", event.target.value)
                      }
                      onFocus={() => handleFieldFocus("endTime")}
                      onBlur={() => handleFieldBlur("endTime")}
                      style={inputOutlineStyle}
                    />
                  </Box>
                  <Button
                    label="Now"
                    size="small"
                    primary={false}
                    disabled={isWriteLocked || serverTimeLoading}
                    onClick={() => handleSetTimeToNow("endTime")}
                  />
                </Box>
                <TimeFormatHint currentCentralTime={currentCentralTime} />
              </FormField>

              {showStudentId && (
                <FormField
                  label="Student ID"
                  htmlFor="pod-notes-student-id"
                  error={fieldErrors.student_id}
                >
                  <TextInput
                    id="pod-notes-student-id"
                    value={notesState.student_id ?? ""}
                    disabled={isWriteLocked}
                    onChange={(event) =>
                      updateField("student_id", event.target.value)
                    }
                    onFocus={() => handleFieldFocus("student_id")}
                    onBlur={() => handleFieldBlur("student_id")}
                    style={inputOutlineStyle}
                  />
                </FormField>
              )}

              <FormField
                label="Live Auditor Name"
                htmlFor="pod-notes-secondary-name"
              >
                <TextInput
                  id="pod-notes-secondary-name"
                  value={notesState.secondaryProctorName}
                  disabled={isWriteLocked}
                  onChange={(event) =>
                    updateField("secondaryProctorName", event.target.value)
                  }
                  onFocus={() => handleFieldFocus("secondaryProctorName")}
                  onBlur={() => handleFieldBlur("secondaryProctorName")}
                  style={inputOutlineStyle}
                />
              </FormField>

              <FormField
                label="Secondary Proctor Link"
                htmlFor="pod-notes-secondary-link"
              >
                <TextInput
                  id="pod-notes-secondary-link"
                  value={notesState.secondaryProctorLink}
                  disabled={isWriteLocked}
                  onChange={(event) =>
                    updateField("secondaryProctorLink", event.target.value)
                  }
                  onFocus={() => handleFieldFocus("secondaryProctorLink")}
                  onBlur={() => handleFieldBlur("secondaryProctorLink")}
                  style={inputOutlineStyle}
                />
              </FormField>
            </Grid>

            <Box direction="row" wrap gap="medium">
              <NotesCheckBox
                label="Did you check ID?"
                checked={notesState.idCheck}
                disabled={isWriteLocked}
                onChange={(checked) =>
                  updateFieldAndAutosave("idCheck", checked)
                }
              />
              <NotesCheckBox
                label="Did you check the room?"
                checked={notesState.roomCheck}
                disabled={isWriteLocked}
                onChange={(checked) =>
                  updateFieldAndAutosave("roomCheck", checked)
                }
              />
              <NotesCheckBox
                label="Did you check wrists, ears, etc?"
                checked={notesState.personCheck}
                disabled={isWriteLocked}
                onChange={(checked) =>
                  updateFieldAndAutosave("personCheck", checked)
                }
              />
              <NotesCheckBox
                label="Did you confirm submission?"
                checked={notesState.confirmSubmission}
                disabled={isWriteLocked}
                onChange={(checked) =>
                  updateFieldAndAutosave("confirmSubmission", checked)
                }
              />
            </Box>
            {SUBMIT_REQUIRED_CHECKBOXES.some(({ key }) => fieldErrors[key]) && (
              <Box gap="xsmall">
                {SUBMIT_REQUIRED_CHECKBOXES.map(({ key }) =>
                  fieldErrors[key] ? (
                    <Text key={key} size="small" color="status-critical">
                      {fieldErrors[key]}
                    </Text>
                  ) : null,
                )}
              </Box>
            )}

            <FormField
              label="Notes"
              htmlFor="pod-notes-text"
              error={fieldErrors.notes}
            >
              <TextArea
                id="pod-notes-text"
                fill
                rows={6}
                value={notesState.notes}
                placeholder="Session notes"
                disabled={isWriteLocked}
                onChange={(event) => updateField("notes", event.target.value)}
                onFocus={() => handleFieldFocus("notes")}
                onBlur={() => handleFieldBlur("notes")}
                style={inputOutlineStyle}
              />
            </FormField>
            {showStudentId && (
              <Box margin={{ top: "xsmall", bottom: "small" }} gap="xsmall">
                <Text size="small" color="dark-4">
                  Check add issue list for note taking
                </Text>
                <Button
                  label="ADD ISSUE"
                  primary
                  color="status-critical"
                  disabled={isWriteLocked}
                  onClick={() => setShowAddIssueModal(true)}
                />
              </Box>
            )}

            <FormField
              label="Live Auditor Notes"
              htmlFor="pod-notes-secondary-notes"
            >
              <TextArea
                id="pod-notes-secondary-notes"
                fill
                rows={6}
                value={notesState.secondaryProctorNotes}
                placeholder="Live auditor notes"
                disabled={isWriteLocked}
                onChange={(event) =>
                  updateField("secondaryProctorNotes", event.target.value)
                }
                onFocus={() => handleFieldFocus("secondaryProctorNotes")}
                onBlur={() => handleFieldBlur("secondaryProctorNotes")}
                style={inputOutlineStyle}
              />
            </FormField>

            <Box direction="row" align="center" gap="small" wrap>
              <Button
                label="Save Notes"
                primary
                color="black"
                onClick={handleSave}
                disabled={saving || isProcessing || isWriteLocked}
              />
              <Button
                label="Submit"
                primary
                color="black"
                onClick={handleSubmit}
                disabled={saving || isProcessing || isWriteLocked}
              />
              {saving && <Text size="small">Saving...</Text>}
              {saveError && (
                <Text size="small" color="status-critical">
                  {saveError}
                </Text>
              )}
              {submitAreaErrorMessages.length > 0 && (
                <Box gap="xsmall">
                  {submitAreaErrorMessages.map((message, index) => (
                    <Text
                      key={`${message}-${index}`}
                      size="small"
                      color="status-critical"
                    >
                      {message}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </CardBody>
      </Card>
    </>
  );
}
